import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@trovata/environments/environment';
import { StablecoinPage } from '../../models/stablecoin.model';
import {
	StablecoinApprovalDecisionValue,
	StablecoinApprovalPolicy,
	StablecoinApprovalState,
	StablecoinBulkApprovalRequest,
	StablecoinBulkApprovalResponse,
	StablecoinTransfer,
	StablecoinTransferCreate,
	StablecoinTransferListFilter,
	StablecoinTransferListView,
} from '../../models/transfer.model';

/**
 * Low-level HTTP service for stablecoin transfers and approvals. Destinations live in StablecoinAddressBookService.
 * State/components should use StablecoinTransfersFacadeService instead.
 */
@Injectable({
	providedIn: 'root',
})
export class StablecoinTransfersService {
	private readonly http: HttpClient = inject(HttpClient);
	private readonly baseUrl: string = environment.stablecoinAPI();

	/** Lists transfers; `filter.view` narrows to the caller's review queue or to open (non-terminal) payments. */
	getTransfers(filter?: StablecoinTransferListFilter, cursor?: string, pageSize?: number): Observable<StablecoinPage<StablecoinTransfer>> {
		let params: HttpParams = new HttpParams();
		if (filter) {
			Object.entries(filter).forEach(([key, value]: [string, string | null | undefined]) => {
				if (value !== null && value !== undefined && value !== '') {
					params = params.set(key, value);
				}
			});
		}
		if (cursor) {
			params = params.set('page_cursor', cursor);
		}
		if (pageSize) {
			params = params.set('page_size', pageSize.toString());
		}
		return this.http.get<StablecoinPage<StablecoinTransfer>>(`${this.baseUrl}/transfers`, { params });
	}

	/** The "Ready for Review" queue: payments the caller can approve, release or clear a hold on (`view=ready_for_review`). */
	getNeedsApprovalTransfers(): Observable<StablecoinPage<StablecoinTransfer>> {
		const params: HttpParams = new HttpParams().set('view', StablecoinTransferListView.readyForReview).set('page_size', '500');
		return this.http.get<StablecoinPage<StablecoinTransfer>>(`${this.baseUrl}/transfers`, { params });
	}

	createTransfer(body: StablecoinTransferCreate, idempotencyKey: string): Observable<StablecoinTransfer> {
		const headers: HttpHeaders = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers`, body, { headers });
	}

	/** Cancels a payment; the optional reason (≤1000 chars) is stored in `status_reason`. */
	cancelTransfer(transferId: string, reason?: string | null): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/cancel`, { reason: reason?.trim() || null });
	}

	/** Retries a failed (retryable) or expired payment as a new payment (201); the old one gets `retried_by_transfer_id`. */
	retryTransfer(transferId: string): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/retry`, {});
	}

	/** Approves or rejects several payments; each runs the normal approval path. Approve never releases. */
	bulkApprovals(body: StablecoinBulkApprovalRequest): Observable<StablecoinBulkApprovalResponse> {
		return this.http.post<StablecoinBulkApprovalResponse>(`${this.baseUrl}/transfers/bulk-approvals`, body);
	}

	/** Returns the transfer's approval requests, oldest first (one per workflow stage; a single null-stage entry for legacy policies). */
	getTransferApprovals(transferId: string): Observable<StablecoinApprovalState[]> {
		return this.http.get<StablecoinApprovalState[]>(`${this.baseUrl}/transfers/${transferId}/approvals`);
	}

	submitTransferApproval(transferId: string, decision: StablecoinApprovalDecisionValue, comment?: string): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/approvals`, { decision, comment: comment ?? null });
	}

	getApprovalPolicy(): Observable<StablecoinApprovalPolicy> {
		return this.http.get<StablecoinApprovalPolicy>(`${this.baseUrl}/approval-policies`);
	}

	/** Clears a rule hold; the transfer returns to pending_approval on the same stage. Caller must be a member of the held stage. */
	clearHold(transferId: string, comment?: string): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/clear-hold`, { comment: comment ?? null });
	}
}
